import { useState, useEffect, useRef } from "react";
import { type SocialLink } from "@/hooks/useContentStore";
import { useRemoteContent } from "@/lib/ContentContext";

interface FooterProps {
  lang: "zh" | "en";
}

const links = [
  { zh: "首页", en: "Home", href: "#banner" },
  { zh: "产品展示", en: "Products", href: "#products" },
  { zh: "关于我们", en: "About Us", href: "#about" },
  { zh: "联系我们", en: "Contact", href: "#contact" },
];

export default function Footer({ lang }: FooterProps) {
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [showAutoPlayHint, setShowAutoPlayHint] = useState(false);
  const [waitingForActivation, setWaitingForActivation] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fadeFrameRef = useRef<number | null>(null);
  const content = useRemoteContent();
  const { friendLinks, socialLinks, footerLegal, bgMusic } = content;
  const showMusicPlayer = bgMusic.enabled;
  const remoteAudioUrl = bgMusic.url?.trim() || null;
  const targetVolume = Math.min(1, Math.max(0, bgMusic.volume ?? 0.4));
  const playbackStrategy = bgMusic.strategy ?? "audible-first";
  // resolvedAudioUrl: 直接使用服务端下发的音频地址
  const [resolvedAudioUrl, setResolvedAudioUrl] = useState<string | null>(remoteAudioUrl);

  const waitForAudioReady = (audio: HTMLAudioElement) => new Promise<void>((resolve) => {
    if (audio.readyState >= 2) {
      resolve();
      return;
    }

    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      audio.removeEventListener("canplay", done);
      audio.removeEventListener("loadeddata", done);
      window.clearTimeout(timer);
      resolve();
    };

    const timer = window.setTimeout(done, 1800);
    audio.addEventListener("canplay", done, { once: true });
    audio.addEventListener("loadeddata", done, { once: true });
    try {
      audio.load();
    } catch {
      done();
    }
  });

  const stopFade = () => {
    if (fadeFrameRef.current !== null) {
      window.cancelAnimationFrame(fadeFrameRef.current);
      fadeFrameRef.current = null;
    }
  };

  const fadeAudioTo = (audio: HTMLAudioElement, volume: number, durationMs: number) => {
    stopFade();
    const start = audio.volume;
    const end = Math.min(1, Math.max(0, volume));
    if (durationMs <= 0 || Math.abs(start - end) < 0.01) {
      audio.volume = end;
      return;
    }

    const startAt = performance.now();
    const tick = (now: number) => {
      const progress = Math.min(1, (now - startAt) / durationMs);
      audio.volume = start + (end - start) * progress;
      if (progress < 1) {
        fadeFrameRef.current = window.requestAnimationFrame(tick);
      } else {
        fadeFrameRef.current = null;
      }
    };

    fadeFrameRef.current = window.requestAnimationFrame(tick);
  };

  useEffect(() => {
    setResolvedAudioUrl(remoteAudioUrl);
  }, [remoteAudioUrl]);

  // When resolvedAudioUrl changes, tear down old audio instance
  useEffect(() => {
    if (audioRef.current) {
      stopFade();
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.volume = 0;
      audioRef.current.muted = true;
      audioRef.current.load();
      setMusicPlaying(false);
      setShowAutoPlayHint(false);
      setWaitingForActivation(false);
      autoPlayDoneRef.current = false;
    }
  }, [resolvedAudioUrl]);

  useEffect(() => {
    return () => {
      stopFade();
    };
  }, []);

  const startAudiblePlayback = async () => {
    const audio = audioRef.current;
    if (!audio || !resolvedAudioUrl) return false;
    await waitForAudioReady(audio);
    audio.muted = false;
    audio.volume = 0;
    if (audio.paused) {
      await audio.play();
    }
    setMusicPlaying(true);
    setShowAutoPlayHint(false);
    setWaitingForActivation(false);
    fadeAudioTo(audio, targetVolume, 2200);
    return true;
  };

  const startGesturePlayback = async () => {
    const audio = audioRef.current;
    if (!audio || !resolvedAudioUrl) return false;

    try {
      return await startAudiblePlayback();
    } catch {
      await waitForAudioReady(audio);
      audio.muted = true;
      audio.volume = 0;
      await audio.play();
      audio.muted = false;
      setMusicPlaying(true);
      setShowAutoPlayHint(false);
      setWaitingForActivation(false);
      fadeAudioTo(audio, targetVolume, 2200);
      return true;
    }
  };

  // 尝试自动播放：先尝试有声，失败后退到静音，再等待首次激活转为有声
  const autoPlayDoneRef = useRef(false);
  useEffect(() => {
    if (!showMusicPlayer || !resolvedAudioUrl) {
      if (audioRef.current) {
        stopFade();
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      autoPlayDoneRef.current = false;
      setMusicPlaying(false);
      setShowAutoPlayHint(false);
      setWaitingForActivation(false);
      return;
    }
    if (playbackStrategy === "manual") {
      if (audioRef.current) {
        stopFade();
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      autoPlayDoneRef.current = false;
      setMusicPlaying(false);
      setShowAutoPlayHint(false);
      setWaitingForActivation(false);
      return;
    }
    if (autoPlayDoneRef.current) return;

    const events = ["click", "keydown", "touchstart", "touchend", "pointerdown", "pointerup"];
    const audio = audioRef.current;
    if (!audio) return;

    audio.loop = true;
    audio.preload = "auto";

    const removeListeners = () => {
      events.forEach((ev) => {
        window.removeEventListener(ev, doPlay);
        document.removeEventListener(ev, doPlay);
      });
      document.removeEventListener("visibilitychange", onVisible);
    };

    const doPlay = () => {
      if (autoPlayDoneRef.current) return;
      startGesturePlayback()
        .then(() => {
          autoPlayDoneRef.current = true;
          removeListeners();
          setShowAutoPlayHint(false);
        })
        .catch(() => {
          autoPlayDoneRef.current = false;
          setShowAutoPlayHint(true);
          setWaitingForActivation(true);
        });
    };

    const onVisible = () => { if (!document.hidden) doPlay(); };

    const addActivationListeners = () => {
      events.forEach((ev) => {
        window.addEventListener(ev, doPlay, { once: true });
        document.addEventListener(ev, doPlay, { once: true });
      });
      document.addEventListener("visibilitychange", onVisible);
    };

    const tryMutedFallback = async () => {
      try {
        await waitForAudioReady(audio);
        audio.muted = true;
        audio.volume = 0;
        await audio.play();
        autoPlayDoneRef.current = false;
        setMusicPlaying(true);
        setShowAutoPlayHint(playbackStrategy === "audible-first");
        setWaitingForActivation(true);
        addActivationListeners();
      } catch {
        autoPlayDoneRef.current = false;
        setMusicPlaying(false);
        setShowAutoPlayHint(true);
        setWaitingForActivation(true);
        addActivationListeners();
      }
    };

    if (playbackStrategy === "muted-fallback") {
      void tryMutedFallback();
      return removeListeners;
    }

    // 先尝试直接有声自动播放，保留“打开即有声”的最佳路径。
    audio.muted = false;
    audio.volume = 0;
    audio.play()
      .then(() => {
        autoPlayDoneRef.current = true;
        setMusicPlaying(true);
        setShowAutoPlayHint(false);
        setWaitingForActivation(false);
        fadeAudioTo(audio, targetVolume, 2200);
      })
      .catch(() => {
        void tryMutedFallback();
      });

    return removeListeners;
  }, [playbackStrategy, resolvedAudioUrl, showMusicPlayer, targetVolume]);

  useEffect(() => {
    if (!audioRef.current || !musicPlaying) return;
    fadeAudioTo(audioRef.current, targetVolume, 900);
  }, [musicPlaying, targetVolume]);

  const toggleMusic = () => {
    if (!resolvedAudioUrl) return;
    const audio = audioRef.current;
    if (!audio) return;
    if (musicPlaying) {
      stopFade();
      audio.pause();
      setMusicPlaying(false);
      setShowAutoPlayHint(false);
      setWaitingForActivation(false);
    } else {
      startAudiblePlayback()
        .then(() => {
          autoPlayDoneRef.current = true;
        })
        .catch(() => {
          setMusicPlaying(false);
          setShowAutoPlayHint(true);
          setWaitingForActivation(true);
        });
    }
  };

  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const visibleSocials = socialLinks.filter((s) => s.visible);
  const [wechatModalOpen, setWechatModalOpen] = useState(false);

  const handleSocialClick = (e: React.MouseEvent, s: SocialLink) => {
    if (s.id === "wechat") {
      e.preventDefault();
      setWechatModalOpen(true);
    }
  };

  return (
    <>
      <audio
        ref={audioRef}
        src={resolvedAudioUrl ?? undefined}
        autoPlay
        muted
        loop
        preload="auto"
        playsInline
        className="hidden"
      />
    <footer id="footer" className="bg-xh-bg-deep border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img
                src="/local-images/c1919b7175e0.png"
                alt="Logo"
                className="h-10 w-auto object-contain"
              />
            </div>
            <p className="text-gray-500 text-sm leading-relaxed max-w-xs mb-5">
              {lang === "zh"
                ? "专注航空发动机与无人机动力总成研发制造，以技术创新驱动中国航空动力产业升级。"
                : "Specialized in aero engine & UAV propulsion R&D and manufacturing, powering China's aerospace industry forward."}
            </p>
            {visibleSocials.length > 0 && (
              <div className="flex items-center gap-3 flex-wrap">
                {visibleSocials.map((s) => (
                  <a
                    key={s.id}
                    href={s.href}
                    rel="nofollow noopener noreferrer"
                    title={s.label}
                    target={s.href.startsWith("mailto:") || s.href.startsWith("weixin://") ? undefined : "_blank"}
                    onClick={(e) => handleSocialClick(e, s)}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-cyan-500/20 text-gray-400 hover:text-cyan-400 transition-all duration-300 cursor-pointer"
                  >
                    <i className={`${s.icon} text-sm`} />
                  </a>
                ))}
              </div>
            )}

            {/* Background Music Player */}
            {showMusicPlayer && (
              <div className="mt-4 inline-flex items-center gap-3 bg-white/5 border border-cyan-500/15 rounded-xl px-3 py-2.5">
                <button
                  onClick={toggleMusic}
                  disabled={!resolvedAudioUrl}
                  title={resolvedAudioUrl ? undefined : (lang === "zh" ? "暂无音乐文件" : "No audio file")}
                  className={`w-8 h-8 flex items-center justify-center rounded-full transition-all flex-shrink-0 ${
                    resolvedAudioUrl
                      ? "bg-cyan-500/15 hover:bg-cyan-500/30 text-cyan-400 cursor-pointer"
                      : "bg-white/5 text-gray-600 cursor-not-allowed"
                  }`}
                >
                  <i className={`text-sm ${musicPlaying ? "ri-pause-fill" : "ri-play-fill"}`} />
                </button>
                {musicPlaying ? (
                  <div className="flex items-end gap-0.5 h-4">
                    {[60, 100, 45, 80, 55].map((h, idx) => (
                      <div
                        key={idx}
                        className="w-0.5 bg-cyan-400 rounded-full animate-pulse"
                        style={{
                          height: `${h}%`,
                          animationDelay: `${idx * 110}ms`,
                          animationDuration: `${600 + idx * 100}ms`,
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  <i className="ri-music-2-line text-gray-600 text-sm" />
                )}
                <span className="text-gray-400 text-xs whitespace-nowrap">
                  {musicPlaying
                    ? (lang === "zh" ? "播放中..." : "Playing...")
                    : (bgMusic.name || (lang === "zh" ? "背景音乐" : "Music"))}
                </span>
              </div>
            )}
            {showMusicPlayer && playbackStrategy !== "manual" && showAutoPlayHint && resolvedAudioUrl && !musicPlaying && (
              <button
                type="button"
                onClick={toggleMusic}
                className="mt-3 flex items-center gap-2 text-[11px] text-cyan-300/90 hover:text-cyan-200 transition-colors cursor-pointer"
              >
                <span className="inline-flex h-2 w-2 rounded-full bg-cyan-400 animate-pulse"></span>
                {lang === "zh" ? "浏览器拦截了自动播放，点击这里开启背景音乐" : "Autoplay was blocked. Click here to start the music."}
              </button>
            )}
            {showMusicPlayer && playbackStrategy !== "manual" && waitingForActivation && resolvedAudioUrl && musicPlaying && (
              <p className="mt-3 text-[11px] text-cyan-200/80">
                {lang === "zh" ? "音乐已预加载，首次触碰页面后会自动恢复声音" : "Music is preloaded and will become audible after your first interaction."}
              </p>
            )}
            {showMusicPlayer && playbackStrategy === "manual" && resolvedAudioUrl && !musicPlaying && (
              <p className="mt-3 text-[11px] text-cyan-200/80">
                {lang === "zh" ? "当前为手动播放模式，访客需点击播放按钮后才会开始播放" : "Manual mode is enabled. Visitors need to press play to start the music."}
              </p>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h4 id="footer-nav">
              <a className="text-white text-sm font-semibold mb-4 block tracking-wide">
                {lang === "zh" ? "快速导航" : "Quick Links"}
              </a>
            </h4>
            <ul className="flex flex-col gap-2.5">
              {links.map((l) => (
                <li key={l.href}>
                  <button
                    onClick={() => scrollTo(l.href)}
                    className="text-gray-500 hover:text-cyan-400 text-sm transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {lang === "zh" ? l.zh : l.en}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Friend Links */}
          <div>
            <h4 id="footer-links">
              <a className="text-white text-sm font-semibold mb-4 block tracking-wide">
                {lang === "zh" ? "友情链接" : "Partner Links"}
              </a>
            </h4>
            <ul className="flex flex-col gap-2.5">
              {friendLinks.map((l, i) => (
                <li key={i}>
                  <a
                    href={l.href}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="text-gray-500 hover:text-cyan-400 text-sm transition-colors cursor-pointer"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>


          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/5 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 relative">
          <p className="text-gray-600 text-xs">{footerLegal.copyright}</p>

          {/* Hidden Admin Hotkey */}
          <a
            href="/admin"
            aria-label="后台入口"
            title="后台入口"
            className="absolute left-1/2 -translate-x-1/2 w-7 h-7 flex items-center justify-center rounded-full bg-transparent hover:bg-white/5 text-transparent hover:text-gray-500 transition-all duration-300 cursor-pointer group"
          >
            <span className="absolute inset-0 rounded-full border border-transparent group-hover:border-white/10 transition-colors duration-300"></span>
            <span className="w-1.5 h-1.5 rounded-full bg-white/5 group-hover:bg-white/20 transition-colors duration-300"></span>
            <i className="ri-settings-3-line absolute text-xs opacity-0 group-hover:opacity-40 transition-opacity duration-300" />
          </a>

          <div className="flex items-center gap-4 flex-wrap justify-center">
            {footerLegal.privacyHref && (
              <a href={footerLegal.privacyHref} rel="nofollow" className="text-gray-600 hover:text-gray-400 text-xs transition-colors cursor-pointer whitespace-nowrap">
                {lang === "zh" ? footerLegal.privacyZh : footerLegal.privacyEn}
              </a>
            )}
            <span className="text-gray-700">|</span>
            {footerLegal.termsHref && (
              <a href={footerLegal.termsHref} rel="nofollow" className="text-gray-600 hover:text-gray-400 text-xs transition-colors cursor-pointer whitespace-nowrap">
                {lang === "zh" ? footerLegal.termsZh : footerLegal.termsEn}
              </a>
            )}
            <span className="text-gray-700">|</span>
            {footerLegal.icpHref && (
              <a href={footerLegal.icpHref} rel="nofollow noopener noreferrer" target="_blank" className="text-gray-600 hover:text-gray-400 text-xs transition-colors cursor-pointer whitespace-nowrap">
                {lang === "zh" ? footerLegal.icpZh : footerLegal.icpEn}
              </a>
            )}
          </div>
        </div>
      </div>
    </footer>

      {/* WeChat QR Modal */}
      {wechatModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setWechatModalOpen(false)}
        >
          <div
            className="bg-xh-bg-modal border border-white/10 rounded-2xl p-6 md:p-8 max-w-sm w-full mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-semibold text-base">
                {lang === "zh" ? "添加微信好友" : "Add on WeChat"}
              </h3>
              <button
                onClick={() => setWechatModalOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all cursor-pointer"
              >
                <i className="ri-close-line text-sm" />
              </button>
            </div>
            <div className="flex flex-col items-center gap-4">
              <div className="w-48 h-48 bg-white rounded-xl p-2 flex items-center justify-center">
                <img
                  src="/local-images/84b4e210946c.png"
                  alt="WeChat QR"
                  className="w-full h-full object-contain"
                />
              </div>
              <p className="text-gray-400 text-sm text-center">
                {lang === "zh"
                  ? "微信号：ch98701"
                  : "WeChat ID: ch98701"}
              </p>
              <p className="text-gray-500 text-xs text-center leading-relaxed">
                {lang === "zh"
                  ? "请使用微信扫一扫，或搜索微信号添加好友"
                  : "Scan with WeChat or search the ID to add"}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
